import { useDispatch } from "react-redux"
import { useQuery } from "@tanstack/react-query"
import { useMemo, useCallback } from "react"
import { noop, get } from "lodash"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { sanitizePositionSet } from "gatherers/helpers"
import { OK } from "helpers/data-statuses"
import { DEFAULT_HORIZON } from "helpers/forecasts/constants"
import useResource from "hooks/resources"

export function useImpliedReturns({ modelId, positionSet, base, from, to, horizon = DEFAULT_HORIZON, hasImpliedReturns = true }) {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()

  const queryFn = useCallback(({ queryKey }) => {
    const [, { modelId, positionSet, base, from, to, horizon }] = queryKey
    return new Promise((resolve, reject) => {
      dispatch(Creators.graphql(accountAlias, getQuery(), {
        modelId,
        positionSet,
        base,
        from,
        to,
        horizon,
      }, resolve, reject))
    })
  }, [dispatch, accountAlias])

  const query = useMemo(() => {
    if (!modelId || !positionSet || !from || !to || !hasImpliedReturns) {
      return null
    }

    const sanitizedPositionSet = sanitizePositionSet(positionSet)
    const sanitizedBase = base ? sanitizePositionSet(base) : null

    return {
      queryKey: ["implied-returns", { modelId, positionSet: sanitizedPositionSet, base: sanitizedBase, from, to, horizon }],
      queryFn,
      select: (data) => get(data, "data.model.simulation.forecast.impliedReturns"),
    }
  }, [modelId, positionSet, base, from, to, horizon, hasImpliedReturns, queryFn])

  const { status, data } = useQuery(
    query
      ? {
        ...query,
        enabled: true,
      }
      : {
        queryKey: ["implied-returns", "disabled"],
        queryFn: noop,
        enabled: false,
      },
  )

  // If the query is disabled (missing required parameters), return OK status
  if (!query) {
    return [OK, null]
  }
  return [data, status]
}

const FORECAST_OTHER_ASSET_FIELDS = `
  id
  expectedPercentReturn
`

const FORECAST_FIELDS = `
  horizon
  total
  equities {
    id {
      ticker
      mic
      internalId
    }
    expectedPercentReturn
  }
  swaps {
    ${FORECAST_OTHER_ASSET_FIELDS}
  }
  indexes {
    ${FORECAST_OTHER_ASSET_FIELDS}
  }`

function getQuery() {
  return `
  query($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!, $horizon: Int!) {
    model(id: $modelId) {
      simulation(positionSet: $positionSet, base: $base, from: $from, to: $to) {
        forecast(on: $to, horizon: $horizon) {
          impliedReturns(riskFactors: MARKET) {
            ${FORECAST_FIELDS}
          }
        }
      }
    }
  }`
}

export const FORECAST_LIST_QUERY = {
  resource: "forecasts",
  url: () => {
    return {
      id: "forecasts",
      query: `query {
        forecasts {
          id
          name
          description
        }
      }`,
    }
  },
}

export function useForecasts() {
  return useResource(FORECAST_LIST_QUERY, "forecasts")
}
